import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Space,
  Button,
  Modal,
  Table,
  notification,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Checkbox } from "antd";
import { Image } from "antd";

import LaxmisKitchenCategoryCreate from "./Create";
import EditCategory from "./Edit";
import CategoryOrdering from "./Ordering";
import Imageurldata from "../../../../../Imageurldata";
import config from "../../../../../config/config";
import axios from "axios";

const { confirm } = Modal;

function LaxmisKitchenCategoriesIndex() {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(1);

  const [editOpen, setEditOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [orderingOpen, setOrderingOpen] = useState(false);

  const moduleName = "Category";
  const moduleNamePlural = "Categories";
  const base_url = `${config.api_url}laxmis-kitchen`;

  /* ===================== FETCH ===================== */
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${base_url}/recipe/all`);
      const sorted = res.data.categories.sort(
        (a, b) => a.position - b.position
      );
      setCategories(sorted);
    } catch (err) {
      notification.error({ message: "Failed to fetch categories" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /* ===================== CREATE ===================== */
  const handleCreate = () => setCreateOpen(true);

  const handleCreateClose = () => {
    setCreateKey((prev) => prev + 1);
    setCreateOpen(false);
    fetchCategories();
  };

  /* ===================== DELETE ===================== */
  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`${base_url}/recipe/category/delete`, {
        data: { id }, // IMPORTANT for DELETE
      });

      notification.success({
        message: `${moduleName} deleted successfully`,
      });

      // BEST PRACTICE → use API response
      const sorted = res.data.categories.sort(
        (a, b) => a.position - b.position
      );
      setCategories(sorted);
    } catch (err) {
      notification.error({
        message: "Delete failed",
      });
    }
  };

  const handleFeaturedToggle = async (record) => {
    try {
      const res = await axios.put(
        `${base_url}/recipe/category/toggle-checked`,
        {
          id: record._id,
        }
      );

      notification.success({
        message: "Category updated",
      });

      // Use response data directly (BEST PRACTICE)
      const sorted = res.data.categories.sort(
        (a, b) => a.position - b.position
      );
      setCategories(sorted);
    } catch (err) {
      notification.error({
        message: "Failed to update category",
      });
    }
  };

  /* ===================== TABLE ===================== */
  const columns = [
    {
      title: "Mark Featured Category",
      dataIndex: "checked",
      key: "checked",
      width: 250,
      render: (_, record) => (
        <Checkbox
          checked={record.checked}
          onChange={() => handleFeaturedToggle(record)}
        />
      ),
    },
    {
      title: "Category Title",
      dataIndex: "categoryTitle",
      key: "categoryTitle",
    },
    {
      title: "Image",
      key: "image",
      width: 250,
      render: (_, record) => {
        const imageUrl =
          Imageurldata + record.categoryImageUrl.replaceAll("\\", "/");

        return (
          <Image
            src={imageUrl}
            width={80}
            height={80}
            style={{ objectFit: "cover", borderRadius: 6 }}
            preview={{
              mask: "Preview",
            }}
            fallback="/no-image.png"
          />
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            onClick={() => {
              setSelectedCategory(record);
              setEditOpen(true);
            }}
          >
            Edit
          </Button>
          <Button type="danger" onClick={() => showDeleteConfirm(record._id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const showDeleteConfirm = (id) => {
    confirm({
      title: `Are you sure you want to delete this ${moduleName}?`,
      icon: <ExclamationCircleOutlined />,
      okText: "YES",
      okType: "danger",
      cancelText: "NO",
      onOk() {
        return handleDelete(id);
      },
    });
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedCategory(null);
    fetchCategories();
  };

  return (
    <div>
      {/* BREADCRUMB */}
      <Breadcrumb style={{ margin: "16px 0" }}>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>{moduleNamePlural}</Breadcrumb.Item>
      </Breadcrumb>

      <Row>
        <Col span={24}>
          <Card title={moduleNamePlural} bordered={false}>
            <Row style={{ marginBottom: 20 }}>
              <Col span={24}>
                <Space>
                  <Button type="primary" onClick={handleCreate}>
                    Add {moduleName}
                  </Button>
                  <Button type="primary" onClick={() => setOrderingOpen(true)}>
                    Order Categories
                  </Button>
                </Space>
              </Col>
            </Row>

            <Table
              loading={loading}
              rowKey="_id"
              columns={columns}
              dataSource={categories}
              footer={() => `Total ${moduleNamePlural}: ${categories.length}`}
            />

            {/* CREATE MODAL */}
            <LaxmisKitchenCategoryCreate
              key={`create${createKey}`}
              isVisible={createOpen}
              handleClose={handleCreateClose}
              base_url={base_url}
              moduleName={moduleName}
            />

            {selectedCategory && (
              <EditCategory
                isVisible={editOpen}
                handleClose={handleEditClose}
                dbData={selectedCategory}
                base_url={base_url}
                moduleName={moduleName}
              />
            )}

            {orderingOpen && (
              <CategoryOrdering
                isOpen={orderingOpen}
                onClose={(updatedCategories) => {
                  setOrderingOpen(false);
                  if (updatedCategories) setCategories(updatedCategories);
                  else fetchCategories(); // fallback
                }}
                categories={categories}
                base_url={base_url}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default LaxmisKitchenCategoriesIndex;
